"use client";

import {
  Textarea as ChakraTextarea,
  FormControl,
  FormLabel,
  Text,
} from "@chakra-ui/react";
import type {
  ControllerRenderProps,
  FieldErrors,
  Path,
  UseFormRegister,
} from "react-hook-form";
import { useThemeTokens } from "@/lib/theme/tokens";

export interface LabeledTextareaProps<T extends Record<string, any>> {
  name: Path<T>;
  label: string;
  errors?: FieldErrors<T>;
  register?: UseFormRegister<T>;
  field?: ControllerRenderProps<T, Path<T>>;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  textColor?: string;
  placeholder?: string;
  required?: boolean;
  rows?: number;
  mb?: number;
  isDisabled?: boolean;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
  maxLength?: number;
  resize?: "none" | "both" | "horizontal" | "vertical";
  "data-testid"?: string;
}

export const LabeledTextarea = <T extends Record<string, any>>({
  name,
  label,
  errors,
  register,
  field,
  value,
  onChange,
  textColor: textColorProp,
  placeholder,
  required = false,
  rows = 3,
  isDisabled = false,
  onFocus,
  mb = 6,
  maxLength,
  resize,
  "data-testid": dataTestId,
}: LabeledTextareaProps<T>) => {
  const theme = useThemeTokens();
  const textColor = textColorProp || theme.text.primary;

  // Helper function to get nested error from path like "students.0.firstName"
  const getNestedError = (
    errors: FieldErrors<T> | undefined,
    path: string,
  ): any => {
    if (!errors) return undefined;
    const parts = path.split(".");
    let current: any = errors;
    for (const part of parts) {
      if (current === null || current === undefined) return undefined;
      // Try to access as array index first, then as property
      const numPart = parseInt(part, 10);
      if (!isNaN(numPart) && Array.isArray(current)) {
        current = current[numPart];
      } else {
        current = current[part];
      }
    }
    return current;
  };

  const error = getNestedError(errors, String(name));
  const isInvalid = !!error;

  const textareaProps = {
    ...(field
      ? field
      : register
        ? register(name)
        : { name, value, onChange }),
    rows,
    placeholder,
    maxLength,
    resize,
    fontSize: "sm",
    fontWeight: "500",
    borderRadius: "8px",
    borderWidth: "1px",
    borderColor: isInvalid ? theme.accent.primary : theme.borderGlass.default,
    bg: "transparent",
    color: theme.text.primary,
    minH: "110px",
    px: 4,
    py: 3.5,
    _placeholder: {
      color: theme.text.tertiary,
      fontWeight: "400",
    },
    _focus: {
      borderColor: theme.accent.primary,
      boxShadow: theme.focusRing,
      bg: `transparent !important`,
      color: `${theme.text.primary} !important`,
    },
    _invalid: {
      borderColor: theme.accent.primary,
    },
    _autofill: {
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      WebkitTextFillColor: theme.text.primary,
      boxShadow: `0 0 0 1000px rgba(0, 0, 0, 0) inset !important`,
      transition: "background-color 600000s 0s, color 600000s 0s",
      caretColor: theme.text.primary,
    },
    sx: {
      "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active":
        {
          WebkitBackgroundClip: "text !important",
          backgroundClip: "text !important",
          WebkitTextFillColor: `${theme.text.primary} !important`,
          boxShadow: `0 0 0 1000px rgba(0, 0, 0, 0) inset !important`,
          transition:
            "background-color 600000s 0s, color 600000s 0s !important",
          caretColor: `${theme.text.primary} !important`,
        },
      "&:-internal-autofill-selected": {
        WebkitBackgroundClip: "text !important",
        backgroundClip: "text !important",
        WebkitTextFillColor: `${theme.text.primary} !important`,
        boxShadow: `0 0 0 1000px rgba(0, 0, 0, 0) inset !important`,
      },
      "&:focus": {
        bg: `transparent !important`,
        color: `${theme.text.primary} !important`,
        boxShadow: `${theme.focusRing} !important`,
      },
      "&:focus:-webkit-autofill": {
        boxShadow: `${theme.focusRing} !important`,
      },
      "@media (hover: hover)": {
        "&:hover:not(:focus):not(:disabled)": {
          borderColor: theme.accent.primaryBorder,
          bg: "transparent",
        },
      },
    },
    transition: "all 0.18s ease-out",
  };

  return (
    <FormControl isInvalid={isInvalid} mb={mb}>
      <FormLabel
        ms="4px"
        mb="5px"
        fontSize="sm"
        fontWeight="500"
        color={textColor}
        letterSpacing="-0.01em"
      >
        {label}
        {required && (
          <Text as="span" color={theme.accent.primary} ml={1}>
            *
          </Text>
        )}
      </FormLabel>

      <ChakraTextarea
        {...textareaProps}
        isDisabled={isDisabled}
        onFocus={onFocus}
        {...(dataTestId ? { "data-testid": dataTestId } : {})}
      />

      {error && (
        <Text
          color={theme.accent.primary}
          fontSize="xs"
          fontWeight="500"
          mt={1.5}
        >
          {error.message as string}
        </Text>
      )}
    </FormControl>
  );
};
